import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import host from "../../global.jsx";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { MessageSquare, X, User, Building2, Phone, Mail, Copy, Check } from "lucide-react";
import Pagination from "./Pagination";

const RatingRow = ({ label, value }) => (
  <div className="flex justify-between text-sm">
    <span className="text-gray-400">{label}</span>
    <span className="text-white font-medium">{value ?? "N/A"}</span>
  </div>
);

// Click-to-copy text pill. `id` must be unique across the page so only the clicked
// instance shows the "copied" checkmark.
const CopyableText = ({ id, value, copiedId, onCopy, placeholder = "—", className = "" }) => {
  if (!value) {
    return <span className="text-gray-500">{placeholder}</span>;
  }
  const isCopied = copiedId === id;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onCopy(id, value);
      }}
      title="Click to copy"
      className={`inline-flex items-center gap-1.5 group/copy text-left hover:text-blue-300 transition-colors ${className}`}
    >
      <span className="truncate">{value}</span>
      {isCopied ? (
        <Check className="w-3.5 h-3.5 text-green-400 shrink-0" />
      ) : (
        <Copy className="w-3.5 h-3.5 shrink-0 opacity-0 group-hover/copy:opacity-70 transition-opacity" />
      )}
    </button>
  );
};

const CandidateFeedbackDetails = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const itemsPerPage = 10;
  const accessToken = Cookies.get("accessToken");
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handleCopy = useCallback((id, value) => {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopiedId(id);
        setTimeout(() => setCopiedId((current) => (current === id ? null : current)), 1500);
      })
      .catch((err) => console.error("Failed to copy:", err));
  }, []);

  const getFeedbackList = useCallback(async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      const response = await axios.get(`${host}/geniehire/candidate-feedback`, {
        params: {
          page: currentPage,
          limit: itemsPerPage,
        },
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.data) {
        setFeedbacks(response.data.feedbacks || []);
        setTotalCount(response.data.total_count || 0);
      }
    } catch (error) {
      console.error("Error fetching candidate feedback:", error);
      if (error.response) {
        const status = error.response.status;
        const detail = error.response.data?.detail || "Failed to fetch candidate feedback";
        if (status === 401) {
          Cookies.remove("accessToken");
          toast.error(`Unauthorized: ${detail}`);
        } else if (status === 404) {
          setFeedbacks([]);
          setTotalCount(0);
        } else {
          setErrorMessage(detail);
          toast.error(`Error: ${detail}`);
        }
      } else {
        setErrorMessage("An unexpected error occurred. Please try again.");
        toast.error("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, currentPage]);

  useEffect(() => {
    getFeedbackList();
  }, [getFeedbackList]);

  const closeDetail = () => setSelectedFeedback(null);

  return (
    <div className="min-h-screen py-4 px-12 mb-10 glass-effect">
      <h1 className="text-2xl font-semibold text-white flex items-center gap-3 mb-10">
        <MessageSquare className="w-6 h-6" />
        Candidate Feedback Details
      </h1>

      {/* Summary */}
      <div className="bg-white/10 p-6 rounded-lg shadow-lg mb-8 border border-gray-600/30 backdrop-blur-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-600/50 backdrop-blur-sm">
            <div className="text-gray-300 font-medium text-sm">Total Feedback Submitted</div>
            <div className="text-2xl font-bold text-white">{totalCount}</div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64 w-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : errorMessage ? (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 text-center text-red-300">
          {errorMessage}
        </div>
      ) : feedbacks.length === 0 ? (
        <div className="bg-white/10 rounded-lg shadow-lg border border-gray-600/30 backdrop-blur-sm p-8 text-center text-gray-300">
          No feedback submitted yet.
        </div>
      ) : (
        <div className="bg-white/10 rounded-lg shadow-lg overflow-hidden border border-gray-600/30 backdrop-blur-sm">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse table-fixed">
              <thead>
                <tr className="bg-gray-800/30">
                  <th className="border-b p-4 text-left text-white w-1/6">Candidate</th>
                  <th className="border-b p-4 text-left text-white w-1/5">Email</th>
                  <th className="border-b p-4 text-left text-white w-1/6">Phone</th>
                  <th className="border-b p-4 text-left text-white w-1/6">Organization</th>
                  <th className="border-b p-4 text-left text-white">Comments</th>
                </tr>
              </thead>
              <tbody>
                {feedbacks.map((feedback) => (
                  <tr
                    key={feedback.interview_id}
                    onClick={() => setSelectedFeedback(feedback)}
                    className="hover:bg-gray-800/20 cursor-pointer"
                  >
                    <td className="border-b p-4 text-white truncate">
                      {feedback.candidate_name || "Unknown"}
                    </td>
                    <td className="border-b p-4 text-gray-200 truncate">
                      {feedback.candidate_email || "—"}
                    </td>
                    <td className="border-b p-4 text-gray-200 truncate">
                      <CopyableText
                        id={`${feedback.interview_id}-phone`}
                        value={feedback.candidate_contact_number}
                        copiedId={copiedId}
                        onCopy={handleCopy}
                      />
                    </td>
                    <td className="border-b p-4 text-gray-200 truncate">
                      {feedback.organization_name || "—"}
                    </td>
                    <td className="border-b p-4 text-gray-200 truncate">
                      {feedback.comments || "No comment"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalItems={totalCount}
          itemsPerPage={itemsPerPage}
        />
      )}

      {/* Feedback Detail Modal */}
      {selectedFeedback && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div
            className="absolute inset-0 bg-black bg-opacity-70"
            onClick={closeDetail}
            style={{ backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}
          ></div>
          <div
            className="glass-effect rounded-lg p-6 w-full max-w-xl mx-auto shadow-lg overflow-y-auto max-h-[80vh] relative"
            style={{
              background: "linear-gradient(to bottom right, rgba(20,30,58,0.97), rgba(11,10,31,0.97))",
              backdropFilter: "blur(40px)",
              WebkitBackdropFilter: "blur(50px)",
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: "10px",
            }}
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <User className="w-5 h-5" />
                {selectedFeedback.candidate_name || "Unknown candidate"}
              </h3>
              <button onClick={closeDetail}>
                <X className="w-5 h-5 text-white cursor-pointer hover:text-gray-300" />
              </button>
            </div>

            <hr className="border-gray-600 mb-4" />

            <div className="bg-gray-800/30 rounded-lg border border-gray-600/50 p-4 mb-4 space-y-2">
              {selectedFeedback.candidate_email && (
                <div className="flex items-center gap-2 text-gray-300 text-sm">
                  <Mail className="w-4 h-4" />
                  {selectedFeedback.candidate_email}
                </div>
              )}
              {selectedFeedback.candidate_contact_number && (
                <div className="flex items-center gap-2 text-gray-300 text-sm">
                  <Phone className="w-4 h-4" />
                  <CopyableText
                    id="modal-phone"
                    value={selectedFeedback.candidate_contact_number}
                    copiedId={copiedId}
                    onCopy={handleCopy}
                  />
                </div>
              )}
              {selectedFeedback.job_role && (
                <div className="text-gray-300 text-sm">Role: {selectedFeedback.job_role}</div>
              )}
              {selectedFeedback.organization_name && (
                <div className="flex items-center gap-2 text-gray-300 text-sm">
                  <Building2 className="w-4 h-4" />
                  {selectedFeedback.organization_name}
                </div>
              )}
              <div className="text-gray-500 text-xs font-mono">
                Interview ID: {selectedFeedback.interview_id}
              </div>
            </div>

            <div className="bg-gray-800/30 rounded-lg border border-gray-600/50 p-4 mb-4 space-y-2">
              <RatingRow label="Overall Rating" value={selectedFeedback.rating} />
              <RatingRow label="Comfort Level" value={selectedFeedback.comfortLevel} />
              <RatingRow label="Difficulty Level" value={selectedFeedback.difficultyLevel} />
              <RatingRow label="Relevance" value={selectedFeedback.relevanceRating} />
              <RatingRow label="Question Quality" value={selectedFeedback.questionQuality} />
            </div>

            <div>
              <div className="text-gray-400 text-sm font-medium mb-2">Comments</div>
              <div className="text-gray-200 text-sm whitespace-pre-wrap leading-relaxed">
                {selectedFeedback.comments || "No comment"}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateFeedbackDetails;
